import { FC } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { Concern } from '../../../../../../apis/messaging/messaging.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';

interface ConcernsListProps {
  concerns: Concern[];
  selectedConcernId: string | null;
  onSelectConcern: (concern: Concern) => void;
  onRaiseConcern: () => void;
  isLoading?: boolean;
  isGuardian?: boolean;
}

const ConcernsList: FC<ConcernsListProps> = ({
  concerns,
  selectedConcernId,
  onSelectConcern,
  onRaiseConcern,
  isLoading = false,
  isGuardian = false,
}) => {
  const { translateText } = useTranslation();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
      data-testid="concerns-list-container"
    >
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6">
          {translateText(
            isGuardian
              ? 'guardian.concerns.myConcerns'
              : 'communications.concerns.title'
          )}
        </Typography>
        {isGuardian && (
          <IconButton
            onClick={onRaiseConcern}
            color="primary"
            data-testid="raise-concern-cta"
            size="small"
          >
            <Add />
          </IconButton>
        )}
      </Box>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={32} />
        </Box>
      ) : concerns.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">
            {translateText('communications.concerns.noConcerns')}
          </Typography>
        </Box>
      ) : (
        <List sx={{ overflow: 'auto', flex: 1 }}>
          {concerns.map((concern) => {
            const id = concern._id || concern.id || '';
            const meta = concern.concern_metadata;
            const subject = meta?.subject || concern.content?.slice(0, 50) || '';
            const isSelected = selectedConcernId === id;

            return (
              <ListItemButton
                key={id}
                selected={isSelected}
                onClick={() => onSelectConcern(concern)}
                data-testid={`concern-list-item-${id}`}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                        {subject}
                      </Typography>
                      <Chip
                        label={translateText(
                          `communications.concerns.priorities.${meta?.priority || 'medium'}`
                        )}
                        size="small"
                        color={
                          meta?.priority === 'high'
                            ? 'error'
                            : meta?.priority === 'medium'
                              ? 'warning'
                              : 'default'
                        }
                      />
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {formatDistanceToNow(new Date(concern.created_at), {
                        addSuffix: true,
                      })}
                      {!concern.is_read && (
                        <Typography
                          component="span"
                          sx={{ ml: 1, color: 'primary.main' }}
                        >
                          •
                        </Typography>
                      )}
                    </Typography>
                  }
                />
              </ListItemButton>
            );
          })}
        </List>
      )}
    </Box>
  );
};

export default ConcernsList;
